import React from "react"
import { Image } from "@chakra-ui/image"
import { Box, Flex, Text, VStack } from "@chakra-ui/layout"

import { PIMSCopy } from "../resources/types"
import ResponsiveBlock from "~components/shared/layout/responsive-block"

import Automation from "~images/pages/pims/graphics/automation.png"
import Communiation from "~images/pages/pims/graphics/communications.png"
import DailyHuddle from "~images/pages/pims/graphics/daily-huddle.png"
import SectionHeading from "~components/shared/text/heading"
import Subheading from "~components/shared/text/subheading"
import Paragraph from "~components/shared/text/paragraph"

interface Props {
    copy: PIMSCopy
}

export default function HowItWorksSection({ copy }: Props) {
    const { pims, image } = copy

    return (
        <ResponsiveBlock pt={{ base: 4, md: 16 }} pb={{ base: 16, md: 36 }}>
            <Flex align="center" justify="center" flexDir="column">
                <VStack spacing={{ base: 4, md: 6 }} mb={12}>
                    <SectionHeading
                        fontWeight="black"
                        size={{ base: "xl", md: "3xl" }}
                    >
                        How It Works
                    </SectionHeading>
                    <Subheading
                        fontWeight="bold"
                        textAlign="center"
                        fontSize={{ base: "md", md: "lg" }}
                    >
                        Chckvet plugs right into your PIMS and website to
                        automate everything.
                    </Subheading>
                </VStack>
                <Flex
                    pt={{ base: 4, md: 8 }}
                    gridGap={{ base: 24, md: 32 }}
                    flexDir={"column"}
                >
                    <OnboardingStep
                        twoWordTitle="WORKS SEAMLESSLY"
                        heading={`Sync with ${pims} so you don't have to think twice`}
                        desc="With Chckvet Connect's auto-syncing, you don't have to worry about entering appointments, clients, or doctors into Chckvet. Chckvet's compatability with your PIMS automates tasks like adding online bookings to your schedule."
                        image={image}
                        reversed
                    />
                    <OnboardingStep
                        twoWordTitle="SAVE TIME"
                        heading="Stop the back-and-forth & automate routine admin tasks"
                        desc={`Because Chckvet is compatible with ${pims}, you can automate admin tasks like appointment confirmations, reminders, report generation.`}
                        image={Automation}
                    />
                    <OnboardingStep
                        twoWordTitle="FULL CUSTOMIZATION"
                        heading="Maintain control over scheduling & communication"
                        desc={`Through our notification center & scheduling rules, choose when & how you want pet owners to communicate and schedule appointments with your practice.`}
                        image={Communiation}
                        reversed
                    />
                    <OnboardingStep
                        twoWordTitle="DAILY INSIGHTS"
                        heading="Start the day off right with the Daily Huddle to help maximize your time."
                        desc={`Our Daily Huddle feature is a great way to start your day. It's a quick and easy way to see what's on your schedule for the day, and what's coming up next.`}
                        image={DailyHuddle}
                    />
                </Flex>
            </Flex>
        </ResponsiveBlock>
    )
}

interface OnboardingStepProps {
    twoWordTitle: string
    heading: string
    desc: string
    image: any
    reversed?: boolean
}

function OnboardingStep({
    twoWordTitle,
    heading,
    desc,
    image,
    reversed,
}: OnboardingStepProps) {
    return (
        <Flex
            flexDir={{ base: "column", md: reversed ? "row-reverse" : "row" }}
            gridGap={[8, 12, 24, 28]}
            align="center"
        >
            <Flex flex={1} display={{ base: "none", md: "flex" }}>
                <Image src={image} w="100%" />
            </Flex>
            <Flex flex={1} minW="320px">
                <Flex flexDir="column" gridGap={{ base: 8, md: 4 }}>
                    <Box>
                        <Text
                            as="h5"
                            fontSize="sm"
                            color="brand.500"
                            fontWeight="black"
                            textTransform="uppercase"
                        >
                            {twoWordTitle}
                        </Text>
                        <SectionHeading
                            as="h6"
                            mt={2}
                            textAlign="left"
                            lineHeight="tallest"
                            fontSize={{ base: "4xl", md: "4xl" }}
                        >
                            {heading}
                        </SectionHeading>
                    </Box>
                    <Image
                        src={image}
                        w="100%"
                        display={{ base: "inline", md: "none" }}
                    />
                    <Paragraph fontSize={{ base: "sm", lg: "lg" }}>
                        {desc}
                    </Paragraph>
                </Flex>
            </Flex>
        </Flex>
    )
}
